import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Brain, Clock, BarChart3, Sparkles, Zap, Target, CheckCircle, AlertTriangle } from 'lucide-react';

const ThinkingDisplay = ({ 
  content,
  isActive = false,
  thinking, 
  isVisible = true, 
  model,
  thinkingTime,
  tokensUsed,
  thinkingSteps = [],
  isStreaming = false,
  confidence = null,
  reasoning_type = null
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Auto-expand for important reasoning
  useEffect(() => {
    if (reasoning_type === 'critical' || confidence && confidence < 0.7) {
      setIsExpanded(true);
    }
  }, [reasoning_type, confidence]);

  // Use content or thinking prop for backward compatibility
  const displayContent = thinking || content;

  if (!isVisible && !isActive && !displayContent && thinkingSteps.length === 0) {
    return null;
  }

  const getModelInfo = () => {
    if (model?.includes('gpt-5')) {
      return {
        name: 'GPT-5 Advanced Thinking',
        description: 'Ph.D.-level reasoning with 80% fewer hallucinations',
        icon: Sparkles,
        color: 'text-emerald-600',
        bgColor: 'from-emerald-50 to-green-50',
        borderColor: 'border-emerald-200'
      };
    } else if (model?.includes('claude-opus-4.1')) {
      return {
        name: 'Claude 4.1 Hybrid Reasoning',
        description: 'State-of-the-art coding & agentic capabilities',
        icon: Brain,
        color: 'text-purple-600',
        bgColor: 'from-purple-50 to-indigo-50',
        borderColor: 'border-purple-200'
      };
    } else if (model?.includes('claude-3-7')) {
      return {
        name: 'Claude 3.7 Reasoning',
        description: 'Hybrid thinking for complex problems',
        icon: Brain,
        color: 'text-blue-600',
        bgColor: 'from-blue-50 to-cyan-50',
        borderColor: 'border-blue-200'
      };
    } else if (model?.includes('o1') || model?.includes('o3')) {
      return {
        name: 'OpenAI Reasoning Model',
        description: 'Always-on extended reasoning',
        icon: Target,
        color: 'text-orange-600',
        bgColor: 'from-orange-50 to-yellow-50',
        borderColor: 'border-orange-200'
      };
    }
    
    return {
      name: 'Extended Thinking',
      description: 'Deep reasoning process',
      icon: Brain,
      color: 'text-purple-600',
      bgColor: 'from-purple-50 to-blue-50',
      borderColor: 'border-purple-200'
    };
  };

  const modelInfo = getModelInfo();
  const IconComponent = modelInfo.icon;

  const formatThinking = (text) => {
    if (!text) return null;
    
    // Enhanced parsing for different thinking patterns
    const sections = text.split(/\n\n+/);
    return sections.map((section, index) => {
      // Detect different section types
      const isHeading = section.match(/^(Step \d+|Phase \d+|Analysis|Conclusion|Let me think|Reasoning|Approach)/i);
      const isSubHeading = section.match(/^(Consider|Note|Important|Key insight)/i);
      const isList = section.includes('•') || section.includes('-') || section.match(/^\d+\./);
      
      return (
        <div key={index} className={`${
          isHeading ? "font-semibold text-gray-800 mt-4 first:mt-0 pb-1 border-b border-gray-200" : 
          isSubHeading ? "font-medium text-gray-700 mt-3 first:mt-0" : 
          "mt-2 first:mt-0"
        }`}>
          {section.split('\n').map((line, lineIndex) => (
            <div key={lineIndex} className={isList ? "ml-2" : ""}>
              {line}
            </div>
          ))}
        </div>
      );
    });
  };

  const renderThinkingSteps = () => {
    if (thinkingSteps.length === 0) return null;
    
    return (
      <div className="space-y-3">
        {thinkingSteps.map((step, index) => (
          <div 
            key={index}
            className={`p-3 rounded-lg border ${
              index === currentStep && isStreaming ? 'bg-yellow-50 border-yellow-300' :
              index < currentStep ? 'bg-green-50 border-green-300' :
              'bg-gray-50 border-gray-300'
            } transition-all duration-300`}
          >
            <div className="flex items-center gap-2 mb-2">
              {index < currentStep ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : index === currentStep && isStreaming ? (
                <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-gray-400" />
              )}
              <span className="font-medium text-sm">
                Step {index + 1}: {step.title || `Reasoning phase ${index + 1}`}
              </span>
              {step.duration && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {step.duration}ms
                </span>
              )}
            </div>
            {step.content && (
              <div className="text-sm text-gray-700 ml-6">
                {step.content}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const getConfidenceDisplay = () => {
    if (!confidence) return null;
    
    const confidencePercent = Math.round(confidence * 100);
    const confidenceColor = confidence >= 0.8 ? 'text-green-600' : confidence >= 0.6 ? 'text-yellow-600' : 'text-red-600';
    const confidenceIcon = confidence >= 0.8 ? CheckCircle : confidence >= 0.6 ? AlertTriangle : AlertTriangle;
    const ConfidenceIcon = confidenceIcon;
    
    return (
      <span className={`flex items-center gap-1 text-xs ${confidenceColor} bg-white px-2 py-1 rounded-full border`}>
        <ConfidenceIcon className="w-3 h-3" />
        {confidencePercent}% confident
      </span>
    );
  };

  return (
    <div className={`mb-4 bg-gradient-to-r ${modelInfo.bgColor || 'from-gray-50 to-gray-100'} border ${modelInfo.borderColor || 'border-gray-200'} rounded-lg overflow-hidden shadow-sm`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white hover:bg-opacity-50 transition-all"
      >
        <div className="flex items-center gap-2">
          {isActive && (
            <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
          )}
          {!isActive && <IconComponent className={`w-4 h-4 ${modelInfo.color || 'text-purple-600'}`} />}
          <div className="flex flex-col">
            <span className={`font-semibold ${modelInfo.color || 'text-purple-600'}`}>
              {isActive ? 'Thinking...' : modelInfo.name}
            </span>
            <span className="text-xs text-gray-600">
              {modelInfo.description}
            </span>
          </div>
          <div className="flex items-center gap-2 ml-2">
            {thinkingTime && (
              <span className="flex items-center gap-1 text-xs text-gray-600 bg-white px-2 py-1 rounded-full border">
                <Clock className="w-3 h-3" />
                {thinkingTime}s
              </span>
            )}
            {tokensUsed && (
              <span className="flex items-center gap-1 text-xs text-blue-600 bg-white px-2 py-1 rounded-full border">
                <BarChart3 className="w-3 h-3" />
                {tokensUsed.toLocaleString()}
              </span>
            )}
            {getConfidenceDisplay()}
            {isStreaming && (
              <span className="flex items-center gap-1 text-xs text-orange-600 bg-white px-2 py-1 rounded-full border">
                <Zap className="w-3 h-3" />
                Thinking...
              </span>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className={`w-4 h-4 ${modelInfo.color || 'text-purple-600'}`} />
        ) : (
          <ChevronDown className={`w-4 h-4 ${modelInfo.color || 'text-purple-600'}`} />
        )}
      </button>
      
      {(isExpanded || isActive) && (displayContent || thinkingSteps.length > 0) && (
        <div className="px-4 pb-4 text-sm text-gray-700 border-t border-gray-200 bg-white bg-opacity-80">
          <div className="max-h-96 overflow-y-auto custom-scrollbar">
            {thinkingSteps.length > 0 ? renderThinkingSteps() : (
              displayContent ? formatThinking(displayContent) : (
                <pre className="whitespace-pre-wrap font-mono text-sm">
                  {displayContent}
                </pre>
              )
            )}
          </div>
        </div>
      )}
      
      {!isExpanded && displayContent && displayContent.length > 100 && (
        <div className={`px-4 pb-3 text-xs ${modelInfo.color || 'text-purple-600'} bg-white bg-opacity-60 border-t ${modelInfo.borderColor || 'border-purple-200'}`}>
          {displayContent.slice(0, 100).replace(/\n/g, ' ')}... 
          <span className="font-medium ml-1">Click to view full reasoning</span>
        </div>
      )}
    </div>
  );
};

export default ThinkingDisplay;